import { SectionCompletionDetail } from '@/api/manualCalculation/types';
import { AvmInferenceData, GraphsData } from '@/api/workOrders/types';
import { TabContext } from '@mui/lab';
import { Box, Tabs } from '@mui/material';
import { SyntheticEvent, useMemo } from 'react';

import { SectionStatusBadge } from '../components/StatusIndicators';
import { StyledTab, StyledTabPanel, TabContent } from '../styles';
import { getTabStatus } from '../utils/tabStatus';
import { ComplementaryInfo } from './components/complementaryInfo';
import { CorrelationMatrix } from './components/correlationMatrix';
import { Equation } from './components/equation';
import { EvaluationResult } from './components/evaluation';
import { InferenceGraphs } from './components/graphs';
import { Hypothesis } from './components/hypothesis';
import { Outliers } from './components/outliers';
import { Residuals } from './components/residuals';
import { Scores } from './components/scores';
import { Statistic } from './components/statistic';
import { VariableDescriptions } from './components/variableDescriptions';
import { VarianceAnalysis } from './components/variance';
import { INFERENCES_TABS } from './constants';
import { InfTabKey } from './types';
import { extractEditableSections } from './utils/avmDataMappers';

interface InferenceScientificTreatmentProps {
  avmData: AvmInferenceData;
  graphs: GraphsData[];
  sectionsStatus: Record<string, SectionCompletionDetail> | undefined;
  activeTab: InfTabKey;
  onTabChange: (tab: InfTabKey) => void;
}

export function InferenceScientificTreatment({
  avmData,
  graphs,
  sectionsStatus,
  activeTab,
  onTabChange,
}: InferenceScientificTreatmentProps): JSX.Element {
  const sections = useMemo(() => extractEditableSections(avmData), [avmData]);
  const {
    evaluationValues,
    scores,
    statistics,
    residuals,
    residualsCurve,
    complementaryInfo,
    outliers,
    variance,
    hypothesis,
    variableDescriptions,
    equation,
    correlation,
  } = sections;

  const tabStatus = useMemo(
    () => getTabStatus(activeTab, sectionsStatus),
    [activeTab, sectionsStatus]
  );

  const handleTabChange = (_: SyntheticEvent, newTab: InfTabKey): void => {
    onTabChange(newTab);
  };

  return (
    <Box>
      <TabContext value={activeTab}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {INFERENCES_TABS.map(({ key, label }) => (
            <StyledTab
              key={key}
              value={key}
              label={<TabContent>{label}</TabContent>}
            />
          ))}
        </Tabs>
        <StyledTabPanel value={activeTab}>
          <SectionStatusBadge status={tabStatus} />
          {activeTab === 'result' && (
            <EvaluationResult evaluationResult={evaluationValues} />
          )}
          {activeTab === 'statistics' && (
            <>
              <Statistic statistic={statistics} />
              <Residuals
                residuals={residuals}
                residualsCurve={residualsCurve}
              />
              <ComplementaryInfo complementaryInfo={complementaryInfo} />
              <Outliers outliers={outliers} />
              <VarianceAnalysis variance={variance} />
              <VariableDescriptions
                variableDescriptions={variableDescriptions}
              />
              <Hypothesis
                hypothesisTests={hypothesis}
                variableNames={[
                  variableDescriptions.dependent_variable.name,
                  ...variableDescriptions.independent_variables.map(
                    (v) => v.name
                  ),
                ]}
              />
              <Equation equation={equation} />
              <CorrelationMatrix
                variableDescriptions={variableDescriptions}
                correlation={correlation}
              />
            </>
          )}
          {activeTab === 'foundation' && <Scores scores={scores} />}
          {activeTab === 'graphs' && (
            <InferenceGraphs
              variableDescriptions={variableDescriptions}
              graphs={graphs}
            />
          )}
        </StyledTabPanel>
      </TabContext>
    </Box>
  );
}
